import React, { useEffect, useState } from 'react';
import './MotivationOverlay.css';
import { OpenRouter } from '@openrouter/sdk';

const MOTIVATION_STORAGE_KEY = 'motivationPhrases';
const MOTIVATION_TIMESTAMP_KEY = 'motivationPhrasesTimestamp';
const UPDATE_INTERVAL_MS = 2 * 60 * 60 * 1000; // 2 hours

const MotivationOverlay = ({ show, onClose }) => {
    const [displayResponse, setDisplayResponse] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [phrases, setPhrases] = useState('');
    const [currentPhraseIndex, setCurrentPhraseIndex] = useState(0);
    const [phrasesArray, setPhrasesArray] = useState([]);

    const fetchMotivationalPhrases = async () => {
        setIsLoading(true);
        try {
            const openRouter = new OpenRouter({
                apiKey: process.env.REACT_APP_OPENROUTER_API_KEY || '',
            });

            const completion = await openRouter.chat.send({
                model: 'google/gemini-3-flash-preview',
                messages: [
                    {
                        role: 'user',
                        content: 'Write 3 motivational phrases for someone who is usually paralyzed from overwhelming: for example: "Just hit the start button, don\'t think about optimality." The phrases could also be about don\'t be afraid of failures, or focusing on small wins. The user is also struggling with obsession over eye floaters as well, phrases that reassure the user can also be output.\nIMPORTANT: your output must contain no prose, just 3 lines in plain text format. Don\'t use markdown, don\'t use lists, or enumeration.',
                    },
                ],
                stream: false,
            });

            const newPhrases = completion.choices[0].message.content;

            // Store in localStorage
            localStorage.setItem(MOTIVATION_STORAGE_KEY, newPhrases);
            localStorage.setItem(MOTIVATION_TIMESTAMP_KEY, Date.now().toString());

            setPhrases(newPhrases);
        } catch (error) {
            console.error('Error fetching motivational phrases:', error);
            setPhrases('Action is the antidote to anxiety, so move your hands first and let your mind catch up whenever it is ready.\nThe drifting shadows in your sight are harmless remnants of life that cannot hinder your progress, so let them pass while you stay focused on your path.\nChoose an imperfect start over a perfect delay, because every small win today builds the bridge to your future.');
        } finally {
            setIsLoading(false);
        }
    };

    const loadOrFetchPhrases = async () => {
        const storedPhrases = localStorage.getItem(MOTIVATION_STORAGE_KEY);
        const storedTimestamp = localStorage.getItem(MOTIVATION_TIMESTAMP_KEY);

        const now = Date.now();
        const shouldUpdate = !storedTimestamp || (now - parseInt(storedTimestamp, 10)) > UPDATE_INTERVAL_MS;

        if (shouldUpdate || !storedPhrases) {
            await fetchMotivationalPhrases();
        } else {
            setPhrases(storedPhrases);
        }
    };

    useEffect(() => {
        if (show) {
            loadOrFetchPhrases();
            setCurrentPhraseIndex(0); // Reset to first phrase when overlay opens
            // Prevent body scrolling when overlay is open
            document.body.style.overflow = 'hidden';
        } else {
            // Re-enable body scrolling when overlay is closed
            document.body.style.overflow = '';
        }

        // Cleanup: restore scrolling when component unmounts
        return () => {
            document.body.style.overflow = '';
        };
    }, [show]);

    // Parse phrases into array when phrases change
    useEffect(() => {
        if (phrases) {
            const lines = phrases.split('\n').filter(line => line.trim() !== '');
            setPhrasesArray(lines);
        }
    }, [phrases]);

    // Slideshow: cycle through phrases every 10 seconds
    useEffect(() => {
        if (!show || phrasesArray.length === 0) return;

        const slideshowInterval = setInterval(() => {
            setCurrentPhraseIndex((prevIndex) => (prevIndex + 1) % phrasesArray.length);
        }, 10000); // 10 seconds

        return () => clearInterval(slideshowInterval);
    }, [show, phrasesArray]);

    // ChatGPT-like typing effect for current phrase
    useEffect(() => {
        if (phrasesArray.length === 0) return;

        const currentPhrase = phrasesArray[currentPhraseIndex] || '';
        setDisplayResponse('');

        let i = 0;
        const typingInterval = setInterval(() => {
            setDisplayResponse(currentPhrase.slice(0, i));
            i = i + 3;

            if (i > currentPhrase.length) {
                clearInterval(typingInterval);
            }
        }, 50);

        return () => clearInterval(typingInterval);
    }, [currentPhraseIndex, phrasesArray]);

    if (!show) return null;

    return (
        <div className="motivationOverlayContainer" onClick={onClose}>
            <div className="motivationOverlayBackdrop" />
            <div className="motivationOverlayContent">
                {isLoading ? (
                    <div className="motivationLoading">Loading inspiration...</div>
                ) : (
                    <>
                        <div className="motivationText">
                            <p className="motivationPhrase">{displayResponse}</p>
                        </div>
                        {phrasesArray.length > 1 && (
                            <div className="motivationIndicator">
                                {phrasesArray.map((_, index) => (
                                    <span
                                        key={index}
                                        className={`dot ${index === currentPhraseIndex ? 'active' : ''}`}
                                    />
                                ))}
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
};

export default React.memo(MotivationOverlay);
